/* eslint-disable jsx-a11y/anchor-is-valid */
import Box from "../Box";
import { Table } from "antd";
import useBreakPoint from "../../utils/useBreakPoint";
import { CPopover, CText } from "@carrefour/design-system-components-react";
import styled from "styled-components";
import { ColumnsType } from "antd/es/table";
import useSWR from "swr";
import { fetcherWithTerritoryKey } from "../../utils/fetcher";
import { SynthesisResponse } from "../../types/synthesis";
import { useTerritories } from "../../context/territoriesContext";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useStores } from "../../context/storesContext";
import { formatKvalue, formatPercentage, getCurrencySuffix } from "../../utils/numberUtils";
import RegionTempo from "../RegionTempo/RegionTempo";


const TextEllipsis = styled(CText)`
  width: 100%;
  height: 100%;
  font-family: system-ui;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: left;
`;
const Popover = styled(CPopover)`
  .c-popover__box {
    width: calc(100% + 80px);
  }
  > * .c-popover__header p {
    font-family: system-ui;
    font-size: small;
  }
`;

const LinkedBox = styled.div`
  cursor: pointer;
  & > * {
    color: var(--Interactive-Content-Main-Active, #0970E6);
  }
`;

type RegionTableProps = {tempo : string};

const getColor = (rank: number, total: number) => {
  const percent = (rank / total) * 100;
  if (percent <= 20) return " #2FB67E";
  if (percent <= 40) return " #8EE2BF";
  if (percent <= 60) return " #FFCE5C";
  if (percent <= 80) return " #F57A7F";
  return " #F25056";
};

const indicators = [
  "turnover",
  "turnoverEvolution",
  "npsMagasin",
  "barometreProduitsDispo",
  "barometrePromoDispo",
  "barometreAffichagePrix",
  "barometreFruitsLegumes",
  "detentionPFT",
  "detentionPGC",
];

const RegionTable = ({ tempo }: RegionTableProps) => {

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { selectStore, stores } = useStores();

  const indicatorLabels: Record<string, string> = {
    turnover: t("commercial.ca"),
    turnoverEvolution: t("operationnel.evolution_ca"),
    npsMagasin: t("operationnel.nps"),
    barometreProduitsDispo: t("operationnel.dispo_produit"),
    barometrePromoDispo: t("operationnel.dispo_promo"),
    barometreAffichagePrix: t("operationnel.affichage_prix"),
    barometreFruitsLegumes: t("operationnel.fl"),
    detentionPFT: t("operationnel.top25"),
    detentionPGC: t("operationnel.top3000"),
  };

  const isBreakpoint = useBreakPoint();

  const { selectedTerritory } = useTerritories();
  const goToStore = (key: string) => {
    selectStore(key);
    navigate("/commercial/live");
  }
  const storesKeys: string[] = stores.map(store => store.storeKey);

  const columns = [
    {
      title: () => (
        <Box>
          <TextEllipsis bold size={"s"}>
            {t("commercial.magasin")}
          </TextEllipsis>
        </Box>
      ),
      dataIndex: "storeName",
      key: "storeName",
      zIndex: 10,
      fixed: "left",
      width: isBreakpoint === "phone" ? 120 : 200,
      sorter: (a: any, b: any) => a.storeName.localeCompare(b.storeName),
      render: (text: string, record) =>
        isBreakpoint === "phone" ? (
          <Popover
            title={text}
            position={"top-right"}
            buttonContent={ storesKeys.includes(record?.key) ?
            <LinkedBox  onClick={()=>goToStore(record?.key)}>
              <TextEllipsis size={"s"}>{text}</TextEllipsis>
            </LinkedBox> : <TextEllipsis size={"s"}>{text}</TextEllipsis>}
          />
        ) : (
          storesKeys.includes(record?.key) ?
          <LinkedBox  onClick={()=>goToStore(record?.key)}>
            <TextEllipsis size={"m"}>{text}</TextEllipsis>
          </LinkedBox> : <TextEllipsis size={"m"}>{text}</TextEllipsis>
        ),
    },
    ...indicators.map((indicator) => ({
      title: () => (
        <Box>
          <TextEllipsis bold size={"s"}>
            {indicatorLabels[indicator]}
          </TextEllipsis>
        </Box>
      ),
      dataIndex: indicator,
      key: indicator,
      width: 130,
      sorter: (a: any, b: any) => {
        const parseSimpleNumber = (val: string): number => {
          if (!val || val === "-") return -Infinity;

          const value = val.replace(",", ".").toUpperCase();

          if (value.endsWith("M€")) {
            return parseFloat(value) * 1000;
          }
          return parseFloat(value);
        };
      
        return parseSimpleNumber(a[indicator]) - parseSimpleNumber(b[indicator]);
      },
      render: (text: string, record: any) => {
        if (indicator === 'turnover' || text === "-") {
          return { props: { style: { backgroundColor: "transparent" } }, children: <TextEllipsis size={isBreakpoint === "phone" ? "s" : "m"}>{text}</TextEllipsis> };
        }

        // Find the store's rank in the sorted list
        const rank = rankedStores[indicator].findIndex((s: { key: any }) => s.key === record.key) + 1;
        const total = rankedStores[indicator].length;
        const color = getColor(rank, total); // Function to determine color based on ranking

        return {
          props: {
            style: { backgroundColor: color,  borderRight: "1px solid white" }

          },
          children: <TextEllipsis size={isBreakpoint === "phone" ? "s" : "m"}>{text}</TextEllipsis>
        };
      },
  }))


  ] as ColumnsType;

  const { data: synthesis } = useSWR<SynthesisResponse>(
    () =>
      selectedTerritory?.key
        ? [
            `/synthesis?range=${tempo}`,
            selectedTerritory?.key as string
          ]
        : null,
    ([url, territoryKey]) =>
      fetcherWithTerritoryKey(url, territoryKey as string)
  );

  const storesMapped = synthesis?.storesSynthesis?.map((synthesisDto) => {
    return {
      key: synthesisDto?.storeKey,
      storeName: synthesisDto.storeName.split("(")?.[0],

      turnover:
        synthesisDto?.data.commercial_data?.turnover != null ? 
                      formatKvalue(
                        Math.abs(synthesisDto?.data.commercial_data?.turnover),""
                      ) + getCurrencySuffix(synthesisDto?.data.commercial_data?.turnover)
                    : 
                      "-",

      turnoverEvolution:
        synthesisDto?.data.commercial_data.turnoverEvolution != null
          ? formatPercentage(synthesisDto?.data.commercial_data.turnoverEvolution) : "-",

      barometreProduitsDispo:
        synthesisDto?.data.nps_data.barometreProduitsDispo != null
          ? new Intl.NumberFormat("fr-FR", {
              maximumFractionDigits: 0,
            }).format(synthesisDto?.data.nps_data.barometreProduitsDispo)
          : "-",

      barometrePromoDispo:
        synthesisDto?.data.nps_data.barometrePromoDispo != null
          ? new Intl.NumberFormat("fr-FR", {
              maximumFractionDigits: 0,
            }).format(synthesisDto?.data.nps_data.barometrePromoDispo)
          : "-",

      barometreAffichagePrix:
        synthesisDto?.data.nps_data.barometreAffichagePrix != null
          ? new Intl.NumberFormat("fr-FR", {
              maximumFractionDigits: 0,
            }).format(synthesisDto?.data.nps_data.barometreAffichagePrix)
          : "-",

      barometreFruitsLegumes:
        synthesisDto?.data.nps_data.barometreFruitsLegumes != null
          ? new Intl.NumberFormat("fr-FR", {
              maximumFractionDigits: 0,
            }).format(synthesisDto?.data.nps_data.barometreFruitsLegumes)
          : "-",

      npsMagasin:
        synthesisDto?.data.nps_data.npsMagasin != null
          ? new Intl.NumberFormat("fr-FR", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 1,
            }).format(synthesisDto?.data.nps_data.npsMagasin)
          : "-",

      detentionPFT:
        synthesisDto?.data.operational_data.pft != null
          ? new Intl.NumberFormat("fr-FR", {
              maximumFractionDigits: 2,
            }).format(synthesisDto?.data.operational_data.pft)
          : "-",

      detentionPGC:
        synthesisDto?.data.operational_data.pgc != null
          ? new Intl.NumberFormat("fr-FR", {
              maximumFractionDigits: 2,
            }).format(synthesisDto?.data.operational_data.pgc)
          : "-",
    };
  });

  const rankedStores = {} as any;
    indicators.forEach((indicator) => {
    // Filter out stores with missing values ("-")
    const sorted = storesMapped?.filter((store) => (store as any)[indicator] !== "-")
        .sort((a, b) => parseFloat(((b as any)[indicator]).replace(",", "."))
          - parseFloat(((a as any)[indicator]).replace(",", "."))); // Sort descending

    rankedStores[indicator] = sorted;
});
  return (
    <Box height={"100%"} width={"100%"} bg={"white"} px={2} py={4}>
      <RegionTempo>
        <Table
          columns={columns}
          dataSource={storesMapped}
          pagination={false}
          sticky
          scroll={{ x: isBreakpoint === "phone" ? 1200 : 1500, y: "calc(100vh - 310px)" }}
          summary={() => {
            return (
              <Table.Summary fixed="bottom">
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0}>
                    <Box>
                      <TextEllipsis bold size={"s"}>
                        {t("territoire.total")}
                      </TextEllipsis>
                    </Box>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1}>
                    <Box>
                      <TextEllipsis bold size={"s"}>
                        {synthesis?.totalTurnover != null
                          ? formatKvalue(Math.abs(synthesis?.totalTurnover), "") +
                            getCurrencySuffix(synthesis?.totalTurnover)
                          : "-"}
                      </TextEllipsis>
                    </Box>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2}>
                    <Box>
                      <TextEllipsis bold size={"s"}>
                        {synthesis?.totalTurnoverEvolution != null
                          ? formatPercentage(synthesis?.totalTurnoverEvolution)
                          : "-"}
                      </TextEllipsis>
                    </Box>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3}>
                    <Box>
                      <TextEllipsis bold size={"s"}>
                        {synthesis?.totalNpsMagasin != null
                          ? new Intl.NumberFormat("fr-FR", {
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 1,
                            }).format(synthesis?.totalNpsMagasin)
                          : "-"}
                      </TextEllipsis>
                    </Box>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={4}>
                    <Box>
                      <TextEllipsis bold size={"s"}>
                        {synthesis?.totalBarometreProduitsDispo != null
                          ? new Intl.NumberFormat("fr-FR", {
                              maximumFractionDigits: 0,
                            }).format(synthesis?.totalBarometreProduitsDispo)
                          : "-"}
                      </TextEllipsis>
                    </Box>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={5}>
                    <Box>
                      <TextEllipsis bold size={"s"}>
                        {synthesis?.totalBarometrePromoDispo != null
                          ? new Intl.NumberFormat("fr-FR", {
                              maximumFractionDigits: 0,
                            }).format(synthesis?.totalBarometrePromoDispo)
                          : "-"}
                      </TextEllipsis>
                    </Box>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={6}>
                    <Box>
                      <TextEllipsis bold size={"s"}>
                        {synthesis?.totalBarometreAffichagePrix != null
                          ? new Intl.NumberFormat("fr-FR", {
                              maximumFractionDigits: 0,
                            }).format(synthesis?.totalBarometreAffichagePrix)
                          : "-"}
                      </TextEllipsis>
                    </Box>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={7}>
                    <Box>
                      <TextEllipsis bold size={"s"}>
                        {synthesis?.totalBarometreFruitsLegumes != null
                          ? new Intl.NumberFormat("fr-FR", {
                              maximumFractionDigits: 0,
                            }).format(synthesis?.totalBarometreFruitsLegumes)
                          : "-"}
                      </TextEllipsis>
                    </Box>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={8}>
                    <Box>
                      <TextEllipsis bold size={"s"}>
                        {synthesis?.totalPft != null
                          ? new Intl.NumberFormat("fr-FR", {
                              maximumFractionDigits: 2,
                            }).format(synthesis?.totalPft)
                          : "-"}
                      </TextEllipsis>
                    </Box>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={9}>
                    <Box>
                      <TextEllipsis bold size={"s"}>
                        {synthesis?.totalPgc != null
                          ? new Intl.NumberFormat("fr-FR", {
                              maximumFractionDigits: 2,
                            }).format(synthesis?.totalPgc)
                          : "-"}
                      </TextEllipsis>
                    </Box>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            );
          }}
        />
      </RegionTempo>  
    </Box>
  );
};

export default RegionTable;